import React, {
  forwardRef,
  ReactNode,
  Ref,
  RefObject,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';
import { useSelector } from 'react-redux';

import { selectCurrentModulesState } from 'src/presentation/components/pages/sidekick/threads/actions/threadsUiSlice';
import useWindowResize from 'src/presentation/components/pages/sidekick/threads/hooks/useWindowResize';
import { relativeThreadBlockHiddenCss, relativeThreadBlockVisibleCss } from '../Layout';
import { FixedContainerRef } from '../../SidekickThreadEditor';

type ChildWrapperProps = {
  children: ReactNode;
  element: HTMLDivElement;
  isSelected: boolean;
  moduleId: string;
  fixedContainerRef: RefObject<FixedContainerRef>;
  moduleContainerRef: RefObject<HTMLDivElement>;
};

export type ChildWrapperRef = {
  recalculate: () => void;
  elementRef: RefObject<HTMLDivElement>;
};

const ChildWrapper = forwardRef(
  (
    { children, element, isSelected, moduleId, fixedContainerRef, moduleContainerRef }: ChildWrapperProps,
    ref: Ref<ChildWrapperRef>
  ) => {
    const [rectPosition, setRectPosition] = useState({ x: 0, y: 0 });

    const modulesStates = useSelector(selectCurrentModulesState);

    const wrapperRef = useRef<HTMLDivElement>(null);

    useImperativeHandle(ref, () => ({
      elementRef: wrapperRef, // ????????? probably don't need this
      recalculate: callback,
    }));

    const callback = useCallback(() => {
      if (
        !element ||
        !modulesStates[moduleId] ||
        !wrapperRef.current ||
        !fixedContainerRef?.current ||
        !fixedContainerRef?.current?.elementRef?.current ||
        !moduleContainerRef.current
      ) {
        return;
      }

      const fixedContainer = fixedContainerRef.current.elementRef.current;

      const shiftX = fixedContainer.getClientRects()[0].x;
      const shiftY = fixedContainer.getClientRects()[0].y;

      const rect = wrapperRef.current.getClientRects()[0];
      const scrollTop = 0;
      const scrollLeft = 0;

      const fromRect = element.getClientRects()[0];
      if (!fromRect) {
        return;
      }

      if (!modulesStates[moduleId].isOpen) {
        const modulesContainer = moduleContainerRef.current;

        let y = fromRect.bottom - shiftY + scrollTop - rect.height;
        if (y < modulesContainer.offsetTop) {
          y = modulesContainer.offsetTop;
        }

        setRectPosition({
          x: fromRect.right - shiftX + 60 + scrollLeft,
          y: y - 5,
        });
        return;
      }

      setRectPosition({
        x: fromRect.right - shiftX + scrollLeft + 60,
        y: fromRect.top - shiftY + scrollTop,
      });

      fixedContainerRef.current?.recalculate();
    }, [element, setRectPosition, modulesStates, moduleId, wrapperRef, fixedContainerRef, moduleContainerRef]);

    useWindowResize({ callback, timeout: 0 });

    return (
      <Wrapper ref={wrapperRef} position={rectPosition} isSelected={isSelected}>
        {children}
      </Wrapper>
    );
  }
);

const Wrapper = styled.div`
  left: ${({ position }) => position.x}px;
  top: ${({ position }) => position.y}px;
  position: absolute;
  display: flex;
  flex-direction: column;
  gap: 24px;
  max-width: 250px;
  min-width: 250px;
  ${p => (p.isSelected ? relativeThreadBlockVisibleCss : relativeThreadBlockHiddenCss)}
`;

export default ChildWrapper;
